import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import {
  StyledEngineProvider,
  ScopedCssBaseline,
  ThemeProvider,
} from '@mui/material';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterMoment from '@mui/lab/AdapterMoment';

import { compose } from 'recompose';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import 'react-toastify/dist/ReactToastify.css';

import NavigationContext from '../context/NavigationContext';
import { withCoachesListContextProvider } from '../context/CoachesListContext';

import { DrawerHeader } from './components/Sidebar';
import Header from './components/Header';
import RightPanel, { COLLAPSED_BAR_WIDTH } from './components/RightPanel';

import LayoutContext from './context/LayoutContext';
import { withLayoutContextProvider } from './context';
import shouldShowRightPanel from './utils/shouldShowRightPanel';

import {
  Container,
  MainBox,
  ContentBox,
  StyledToastContainer,
} from './styles';

import theme from './theme';

const ToolsLayoutContent = ({
  children,
  sidebarComponent,
}) => {
  const {
    rightPanelWidth,
    isChatPanelOpen,
  } = useContext(LayoutContext);
  const { routeType } = useContext(NavigationContext);

  const showRightPanel = shouldShowRightPanel({ routeType });

  // Calculate right margin for main content
  const getRightMargin = () => {
    if (!showRightPanel) {
      return 0;
    }
    if (!isChatPanelOpen) {
      return COLLAPSED_BAR_WIDTH;
    }
    return rightPanelWidth;
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {/*
          ScopedCssBaseline must sit INSIDE ThemeProvider. It reads background.default,
          text.primary and the theme typography; outside the provider it silently falls
          back to MUI's stock palette, so the ink text colour and our typography never
          reach the scoped root. background.default happens to be white either way, so
          the visible symptom is the text colour, not the canvas.
          src/pages/InstagramMedia and src/pages/InstagramPrivacyPolicy mount this same
          theme and use the same order — keep the three in step.
        */}
        <ScopedCssBaseline>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Container>
              <Header showMenuIcon={!!sidebarComponent} />
              {sidebarComponent}
              <MainBox
                component="main"
                $rightMargin={getRightMargin()}
              >
                <DrawerHeader />
                <ContentBox>
                  {children}
                  <StyledToastContainer />
                </ContentBox>
              </MainBox>
              {showRightPanel && <RightPanel />}
            </Container>
          </LocalizationProvider>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

ToolsLayoutContent.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarComponent: PropTypes.element,
};

ToolsLayoutContent.defaultProps = {
  sidebarComponent: null,
};

const ToolsLayout = compose(
  withLayoutContextProvider,
  withCoachesListContextProvider,
)(ToolsLayoutContent);

export default ToolsLayout;
